import Vue from 'vue'
import VueI18n, { I18nOptions, LocaleMessages } from 'vue-i18n'
import { formatDate } from 'date-fns'
import { TZDate } from '@date-fns/tz'
import { BaseDatetimeFormatOptions, BaseHook, BaseUserModule } from '@/types'
import { loadModuleConfigs } from '@/utils/config-util'
import { createHooks } from '@/utils/hook-util'

const languageHooks = createHooks<string>()

export default class BaseVueI18n extends VueI18n {
  private moduleNames: string[]

  constructor(i18nOptions: I18nOptions = {}) {
    i18nOptions.locale = 'dynamic'
    i18nOptions.fallbackLocale = 'none'
    i18nOptions.silentTranslationWarn = true

    super(i18nOptions)

    this.moduleNames = []
  }

  setDefaultModuleNames(defaultModuleNames: string[]): void {
    this.loadModuleMessages(defaultModuleNames)
  }

  setUserModules(userModules: BaseUserModule[]): void {
    this.loadModuleMessages(userModules.map(({ moduleName }) => moduleName))
  }

  setLanguage(language: string): void {
    if (language === this.locale) return

    this.locale = language

    this.loadModuleMessages(this.moduleNames)

    languageHooks.execute(this.locale)
  }

  registerLanguageHook(languageHook: BaseHook<string>): void {
    languageHooks.register(languageHook)
  }

  df(date: Date, options: BaseDatetimeFormatOptions): string {
    const { pattern, timeZone } = options

    return formatDate(new TZDate(date, timeZone), pattern)
  }

  private loadModuleMessages(moduleNames: string[]): void {
    const { locale } = this

    loadModuleConfigs<LocaleMessages>(`/messages/${locale}`, moduleNames, ({ moduleName, moduleValue }) => {
      this.mergeLocaleMessage(locale, { [moduleName]: moduleValue })
    })

    moduleNames
      .filter((moduleName) => !this.moduleNames.includes(moduleName))
      .forEach((moduleName) => this.moduleNames.push(moduleName))
  }
}

Vue.use(BaseVueI18n)

export const baseVueI18n = new BaseVueI18n()

declare module 'vue/types/vue' {
  interface Vue {
    $df: typeof BaseVueI18n.prototype.df
  }
}

Vue.prototype.$df = (date: Date, options: BaseDatetimeFormatOptions): string => baseVueI18n.df(date, options)